import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent, stringItems } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import { normalizeDocText } from '../utils/documents';
import heroFallbackImage from '../components/assets/522.png';
import manifestoFallbackImage from '../components/assets/11.jpg';
import iconSustainability from '../components/assets/icon-sustainability.png';
import iconPerson from '../components/assets/icon-person.png';
import iconHandshake from '../components/assets/icon-handshake.png';

const PAGE_SLUG = 'surdurulebilirlik';

// Banner alti serit: sutun basliklari ve ISO rozetleri ayri kayitlarda tutulur.
const HERO_PILLAR_SLOTS = 5;
const HERO_CHIP_SLOTS = 4;

const DEFAULT_HERO = {
  eyebrow: 'Sürdürülebilirlik · ESG',
  title: 'Doğaya saygı, ilerlemenin tek yolu',
  description:
    'TBH Port Adana, yenilenebilir enerji, elektrikli ekipman dönüşümü ve düşük karbon operasyonlarıyla geleceğin yeşil limanını bugünden inşa ediyor. Doğaya verdiğimiz değer, ticarete verdiğimiz değer kadar büyük.',
  pillars: ['Enerji', 'İklim', 'Çevre', 'İnsan', 'Biyoçeşitlilik'],
  chips: ['ISO 9001', 'ISO 14001', 'ISO 45001', 'ISO 50001'],
};

// Manifesto pill ve paragraflari portalda satir bazli kayitlarda tutulur.
const MANIFESTO_PILL_SLOTS = 3;
const MANIFESTO_PARAGRAPH_SLOTS = 3;

const DEFAULT_MANIFESTO = {
  eyebrow: 'SÜRDÜRÜLEBİLİRLİK MANİFESTOMUZ',
  title: 'Sürdürülebilir Operasyonlar',
  imageBadge: 'Çevresel Sorumluluk',
  pills: ['ÇEVRESEL SORUMLULUK', 'SOSYAL DEĞER', 'ENERJİ TASARRUFU'],
  paragraphs: [
    'TBH Port Adana olarak operasyonlarımızda çevresel ve sosyal etkimizi ölçüyor, yönetiyor ve sürekli iyileştiriyoruz.',
    'Enerji tüketimi ve emisyonlarımızı ekipman bazında izleyerek operasyonel verimliliği artırıyor, elektrifikasyon ve yakıt dönüşümü ile düşük karbonlu liman modeline geçiş sağlıyoruz.',
    'ISO yönetim sistemleri kapsamında yürüttüğümüz süreçlerle, çevresel performansımızı veri temelli olarak yönetiyor ve uluslararası standartlara uyumlu şekilde geliştiriyoruz.',
  ],
  footerPrimary: 'DÜRÜSTLÜK ÜZERİNE KURULU TİCARET',
  footerSecondary: 'GELECEK İÇİN İNŞA EDİLDİ',
};

// Baslikta isaretleme yoksa istenen sirali kelime yesil vurgu icin <em> ile sarilir.
function highlightWord(value?: string, position = 1) {
  const text = (value || '').trim();
  if (!text || text.includes('<')) return text;

  const words = text.split(/\s+/);
  if (words.length < 2) return text;

  const index = Math.min(position, words.length) - 1;
  return words.map((word, order) => (order === index ? `<em>${word}</em>` : word)).join(' ');
}

const DEFAULT_ESG_INTRO = {
  eyebrow: 'ESG',
  title: 'ESG çerçevemiz',
  description:
    'Çevresel sorumluluk, sosyal değer ve şeffaf yönetişim — sürdürülebilirlik stratejimizin üç eşit sütunu. Her birinde ölçülebilir hedefler, somut yatırımlar.',
};

// Kart basligi, aciklamasi ve maddeleri servisten gelir; ikon, ust etiket ve
// renk vurgusu portaldaki kart sirasina gore sabittir.
const ESG_CARD_SPECS = [
  {
    blockKey: 'esg_environmental',
    eyebrow: 'ENVIRONMENTAL',
    accent: 'green',
    icon: iconSustainability,
    title: 'Çevresel Etki Yönetimi',
    description:
      'Operasyonlarımızın çevresel etkilerini ölçüyor ve ISO 14001 kapsamında sistematik olarak yönetiyoruz.',
    items: [
      'Enerji ve emisyon takibi',
      'Su ve atıksu yönetimi',
      'MARPOL uyumlu atık alım hizmetleri',
      'Toz, gürültü ve emisyon kontrolü',
    ],
  },
  {
    blockKey: 'esg_social',
    eyebrow: 'SOCIAL',
    accent: 'navy',
    icon: iconPerson,
    title: 'İnsan ve İş Güvenliği',
    description:
      'Çalışan güvenliği ve refahı operasyonlarımızın temel önceliğidir. Tüm faaliyetler ISO 45001 kapsamında yürütülmekte ve saha riskleri sürekli izlenmektedir.',
    items: [
      'ISO 45001 İSG sistemi',
      'Saha operasyonlarında risk yönetimi',
      'Eğitim ve farkındalık programları',
      'Yerel istihdam ve paydaş katılımı',
    ],
  },
  {
    blockKey: 'esg_governance',
    eyebrow: 'GOVERNANCE',
    accent: 'green',
    icon: iconHandshake,
    title: 'Yönetişim ve Uyumluluk',
    description:
      'Sürdürülebilirlik, entegre yönetim sistemi kapsamında ele alınmakta; ulusal mevzuat ve uluslararası standartlara uyum sağlanmaktadır.',
    items: [
      'Entegre Yönetim Sistemi (ISO 9001, 14001, 45001, 50001)',
      'Uluslararası performans standartlarına uyum',
      'Etik kurallar ve uyum politikaları',
      'Veri yönetimi ve raporlama',
    ],
  },
];

// Ust yesil serit: baslik kayit basliginda, alt satirlar items veya "\n" ayrilmis icerikte.
const HIGHLIGHT_SLOTS = 4;

const DEFAULT_HIGHLIGHTS = [
  { title: 'Su', lines: ['Su', 'Verimliliği'] },
  { title: 'Atık', lines: ['Sıfır Atık', 'Sertifikası'] },
  { title: 'Enerji', lines: ['Yeşil Enerji ile', 'Çalışan Ekipmanlar'] },
  { title: 'ISO', lines: ['9001 · 14001', '45001 · 50001'] },
];

// Odak alanlari: numara sirasi portaldaki "01 ALAN" kayitlariyla ayni.
const DEFAULT_FOCUS = {
  eyebrow: 'BM SÜRDÜRÜLEBİLİR KALKINMA AMAÇLARI',
  title: 'Odaklandığımız Sürdürülebilirlik Alanları',
  description:
    'Birleşmiş Milletler Sürdürülebilir Kalkınma Amaçları (SKA) çerçevesinde operasyonlarımızla doğrudan katkı sağladığımız hedefler.',
};

const DEFAULT_FOCUS_CARDS = [
  'Erişilebilir &\nTemiz Enerji',
  'İnsana Yakışır İş\n& Ekonomik Büyüme',
  'Sanayi, Yenilikçilik\n& Altyapı',
  'Sürdürülebilir\nŞehir & Topluluk',
  'Sorumlu Üretim\n& Tüketim',
  'İklim\nEylemi',
  'Sudaki\nYaşam',
  'Amaçlar İçin\nOrtaklıklar',
];

const DEFAULT_POLICY = {
  title: 'EYS Politikamızı detaylı inceleyin',
  buttonText: 'EYS Politikası',
};

// Portal kart basliklarini "01 KART - " oneki ile kaydeder, sitede onek gosterilmez.
function cardTitle(value?: string) {
  return (value || '').replace(/^\d+\s*KART\s*-\s*/i, '').trim();
}

// Portal cok satirli alanlari kaydederken gercek yeni satir da "\n" metni de kullanabilir.
function splitLines(value?: string) {
  return (value || '')
    .split(/\\n|\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

// Servisten gelen ilk dolu listeyi secer, hepsi bossa son sirayi (varsayilan) dondurur.
function firstFilledList(...candidates: string[][]) {
  return candidates.find((list) => list.length > 0) || [];
}

// ESG kart maddeleri portalda satir bazli kayitlarda tutulur (esg_..._madde_1, _2 ...).
const ESG_ITEM_SLOTS = 8;
const ESG_ITEM_SLOT_INDEXES = Array.from({ length: ESG_ITEM_SLOTS }, (_, index) => index + 1);

export function SustainabilityPage() {
  const { content, getPage, getBlock } = useSiteContent();
  const { t, tList } = useLanguage();
  const page = getPage(PAGE_SLUG);

  // Banner alani: gorsel portalda sayfa kaydinda (Hero Gorsel URL) veya ayri bir
  // icerik blogunda tutulabilir; ikisi de yoksa paketlenmis gorsele dusulur.
  const heroImageBlock = getBlock(PAGE_SLUG, 'sustainability_hero_image');
  const heroImage =
    mediaUrl(heroImageBlock?.content || heroImageBlock?.imageUrl) ||
    mediaUrl(page?.heroImage) ||
    heroFallbackImage;

  // Portalda satir bazli tutulan kayitlari (…_1, …_2) sirasiyla okur, bos olanlari atlar.
  const slotContents = (blockKey: (slot: number) => string, slots: number) =>
    Array.from({ length: slots }, (_, index) =>
      getBlock(PAGE_SLUG, blockKey(index + 1))?.content?.trim(),
    ).filter((value): value is string => Boolean(value));

  const heroEyebrow = getBlock(PAGE_SLUG, 'sustainability_hero_eyebrow');
  const heroTitle = getBlock(PAGE_SLUG, 'sustainability_hero_title');
  const heroDescription = getBlock(PAGE_SLUG, 'sustainability_hero_description');
  const heroPillars = slotContents(
    (slot) => `sustainability_hero_pillar_${slot}`,
    HERO_PILLAR_SLOTS,
  );
  const heroChips = slotContents((slot) => `sustainability_hero_chip_${slot}`, HERO_CHIP_SLOTS);

  const pillars = heroPillars.length > 0 ? heroPillars : DEFAULT_HERO.pillars;
  const chips = heroChips.length > 0 ? heroChips : DEFAULT_HERO.chips;

  const manifestoEyebrow = getBlock(PAGE_SLUG, 'sustainability_manifesto_eyebrow');
  const manifestoTitle = getBlock(PAGE_SLUG, 'sustainability_manifesto_title');
  const manifestoImage = getBlock(PAGE_SLUG, 'sustainability_manifesto_image');
  const manifestoBadge = getBlock(PAGE_SLUG, 'sustainability_manifesto_image_badge');
  const manifestoFooterPrimary = getBlock(PAGE_SLUG, 'sustainability_manifesto_footer_primary');
  const manifestoFooterSecondary = getBlock(PAGE_SLUG, 'sustainability_manifesto_footer_secondary');
  const manifestoPills = slotContents(
    (slot) => `sustainability_manifesto_pill_${slot}`,
    MANIFESTO_PILL_SLOTS,
  );
  const manifestoParagraphs = slotContents(
    (slot) => `sustainability_manifesto_paragraph_${slot}`,
    MANIFESTO_PARAGRAPH_SLOTS,
  );

  const manifestoImageUrl =
    mediaUrl(manifestoImage?.content || manifestoImage?.imageUrl) || manifestoFallbackImage;
  const pills = manifestoPills.length > 0 ? manifestoPills : DEFAULT_MANIFESTO.pills;
  const paragraphs =
    manifestoParagraphs.length > 0 ? manifestoParagraphs : DEFAULT_MANIFESTO.paragraphs;

  const esgEyebrow = getBlock(PAGE_SLUG, 'sustainability_esg_eyebrow');
  const esgTitle = getBlock(PAGE_SLUG, 'sustainability_esg_title');
  const esgDescription = getBlock(PAGE_SLUG, 'sustainability_esg_description');

  // Satir bazli madde kayitlari; yoksa eski JSON items alanina dusulur.
  const esgCardItems = (blockKey: string) => {
    const rows = ESG_ITEM_SLOT_INDEXES.map((slot) =>
      getBlock(PAGE_SLUG, `${blockKey}_madde_${slot}`)?.content?.trim(),
    ).filter((value): value is string => Boolean(value));

    return rows.length > 0 ? rows : stringItems(getBlock(PAGE_SLUG, blockKey)?.items);
  };

  const esgCards = ESG_CARD_SPECS.map((spec) => {
    const block = getBlock(PAGE_SLUG, spec.blockKey);
    const items = esgCardItems(spec.blockKey);
    return {
      ...spec,
      title: cardTitle(block?.title) || spec.title,
      description: block?.content?.trim() || spec.description,
      items: items.length > 0 ? items : spec.items,
      documents: block?.documents,
    };
  });

  const highlights = DEFAULT_HIGHLIGHTS.slice(0, HIGHLIGHT_SLOTS).map((fallback, index) => {
    const block = getBlock(PAGE_SLUG, `sustainability_highlight_card_${index + 1}`);
    return {
      key: `highlight-${index + 1}`,
      title: block?.title?.trim() || fallback.title,
      // Guncel/cevrilmis metin Aciklama'dadir; Ekstra.items eski kopya olabilir
      lines: firstFilledList(
        splitLines(block?.content),
        stringItems(block?.items).map((line) => line.trim()).filter(Boolean),
        fallback.lines,
      ),
    };
  });

  const policyTitle = getBlock(PAGE_SLUG, 'sustainability_policy_title');
  const policyButtonText = getBlock(PAGE_SLUG, 'sustainability_policy_button_text');
  const policyButtonLink = getBlock(PAGE_SLUG, 'sustainability_policy_button_link');
  const existingPolicyDocument = content.documents?.find((doc) => {
    const title = normalizeDocText(`${doc.title} ${doc.fileName || ''}`);
    return (
      doc.isPublished !== false &&
      doc.fileUrl &&
      doc.fileUrl !== '#' &&
      (title.includes('entegre yonetim sistemi politika') ||
        title.includes('integrated management system policy') ||
        /^(eys|ims)[\s_.-]/.test(title))
    );
  });
  const policyDocumentPath = policyButtonLink?.content?.trim();
  const policyDocumentUrl =
    policyDocumentPath && policyDocumentPath !== '/' && policyDocumentPath !== '#'
      ? mediaUrl(policyDocumentPath)
      : mediaUrl(existingPolicyDocument?.fileUrl);

  const focusEyebrow = getBlock(PAGE_SLUG, 'sustainability_focus_eyebrow');
  const focusTitle = getBlock(PAGE_SLUG, 'sustainability_focus_title');
  const focusDescription = getBlock(PAGE_SLUG, 'sustainability_focus_description');
  const focusCards = DEFAULT_FOCUS_CARDS.map((fallback, index) => {
    const block = getBlock(PAGE_SLUG, `sustainability_focus_card_${index + 1}`);
    return {
      key: `focus-${index + 1}`,
      code: String(index + 1).padStart(2, '0'),
      accent: index % 2 === 0 ? 'green' : 'navy',
      lines: firstFilledList(splitLines(block?.content), splitLines(fallback)),
    };
  });

  return (
    <>
      <section
        className="sustainability-hero"
        style={{ backgroundImage: `url('${heroImage}')` }}
      >
        <div className="sustainability-hero-overlay" />
        <div className="container sustainability-hero-inner">
          <div className="eyebrow sustainability-hero-eyebrow">
            {t(heroEyebrow?.content?.trim() || page?.title || DEFAULT_HERO.eyebrow)}
          </div>
          <h1
            className="sustainability-hero-title"
            dangerouslySetInnerHTML={{
              __html: highlightWord(
                t(heroTitle?.content?.trim() || page?.heroTitle || DEFAULT_HERO.title),
              ),
            }}
          />
          <p className="sustainability-hero-lead">
            {t(heroDescription?.content?.trim() || page?.heroSubtitle || DEFAULT_HERO.description)}
          </p>
          <div className="sustainability-hero-pillars">
            {tList(pillars).map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <div className="sustainability-hero-chips">
            {chips.map((chip) => (
              <span className="sustainability-hero-chip" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="manifesto-section">
        <div className="container">
          <div className="manifesto-layout">
            <div className="manifesto-image-wrap">
              <img
                src={manifestoImageUrl}
                alt={t(manifestoTitle?.content || DEFAULT_MANIFESTO.title)}
                className="manifesto-image"
              />
              <div className="manifesto-image-badge">
                {t(manifestoBadge?.content || DEFAULT_MANIFESTO.imageBadge)}
              </div>
            </div>

            <div className="manifesto-copy">
              <div className="manifesto-eyebrow">
                {t(manifestoEyebrow?.content || DEFAULT_MANIFESTO.eyebrow)}
              </div>
              <h2
                className="manifesto-title"
                dangerouslySetInnerHTML={{
                  __html: highlightWord(
                    t(manifestoTitle?.content || DEFAULT_MANIFESTO.title),
                  ),
                }}
              />
              <div className="manifesto-pills">
                {tList(pills).map((pill) => (
                  <span key={pill}>{pill}</span>
                ))}
              </div>
              {tList(paragraphs).map((paragraph) => (
                <p className="manifesto-text" key={paragraph}>
                  {paragraph}
                </p>
              ))}
              <div className="manifesto-signature">
                <b>{t(manifestoFooterPrimary?.content || DEFAULT_MANIFESTO.footerPrimary)}</b>
                <span>
                  {t(manifestoFooterSecondary?.content || DEFAULT_MANIFESTO.footerSecondary)}
                </span>
              </div>
              <DocumentLinks documents={manifestoImage?.documents} />
            </div>
          </div>
        </div>
      </section>

      <section className="esg-section">
        <div className="container">
          <div className="esg-section-eyebrow">
            {t(esgEyebrow?.content || DEFAULT_ESG_INTRO.eyebrow)}
          </div>
          <h2
            className="esg-section-title"
            dangerouslySetInnerHTML={{
              __html: highlightWord(t(esgTitle?.content || DEFAULT_ESG_INTRO.title)),
            }}
          />
          <p className="esg-section-desc">
            {t(esgDescription?.content || DEFAULT_ESG_INTRO.description)}
          </p>
          <div className="esg-grid">
            {esgCards.map((card) => (
              <div className={`esg-card esg-card--${card.accent}`} key={card.blockKey}>
                <div className="esg-card-icon" aria-hidden="true">
                  <img src={card.icon} alt="" />
                </div>
                <div className="esg-card-eyebrow">{card.eyebrow}</div>
                <h3>{t(card.title)}</h3>
                <p className="esg-card-desc">{t(card.description)}</p>
                <ul>
                  {tList(card.items).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <DocumentLinks documents={card.documents} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="green-strip">
        <div className="container green-strip-grid">
          {highlights.map((item) => (
            <div className="green-strip-item" key={item.key}>
              <b>{t(item.title)}</b>
              <div className="green-strip-line" aria-hidden="true" />
              {tList(item.lines).map((line) => (
                <span key={line}>{line}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="focus-section">
        <div className="container">
          <div className="focus-section-eyebrow">
            {t(focusEyebrow?.content || DEFAULT_FOCUS.eyebrow)}
          </div>
          <h2
            className="focus-section-title"
            dangerouslySetInnerHTML={{
              __html: highlightWord(t(focusTitle?.content || DEFAULT_FOCUS.title), 2),
            }}
          />
          <p className="focus-section-desc">
            {t(focusDescription?.content || DEFAULT_FOCUS.description)}
          </p>
          <div className="focus-grid">
            {focusCards.map((card) => (
              <div className={`focus-card focus-card--${card.accent}`} key={card.key}>
                <b>{card.code}</b>
                <div className="focus-card-line" aria-hidden="true" />
                {tList(card.lines).map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="policy-cta">
        <div className="container policy-cta-inner">
          <h2>{t(policyTitle?.content || DEFAULT_POLICY.title)}</h2>
          <a
            href={policyDocumentUrl}
            className="policy-cta-button"
            download
            aria-disabled={policyDocumentUrl ? undefined : true}
          >
            {t(policyButtonText?.content || DEFAULT_POLICY.buttonText)}
          </a>
        </div>
      </section>
    </>
  );
}
